import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon, PencilEditIcon, Tick02Icon } from "@hugeicons-pro/core-bulk-rounded";
import type { IconSvgElement } from "@hugeicons/react";

import { useState } from "react";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatMetric } from "@/lib/format";
import { onTrack as computeOnTrack } from "@/lib/benchmarks";
import { cn } from "@/lib/utils";
import type { MetricUnit } from "@/lib/api/types";

export interface BenchmarkCellProps {
  label: string;
  actual: number | null;
  /** null when Prado has not set a target yet — the cell then asks for one
   *  instead of inventing a plausible-looking number. */
  target: number | null;
  unit: MetricUnit;
  higherIsBetter: boolean | null;
  note?: string;
  money?: (v: number | null | undefined) => string;
  /** When provided the target becomes inline-editable ("edit mode"). Receives
   *  the RAW (unscaled, monthly) value the user typed. */
  onSaveTarget?: (value: number) => Promise<void> | void;
  /** The raw target to seed the editor with (defaults to `target`). Pages that
   *  display a window-scaled target pass the unscaled monthly value here. */
  editValue?: number;
}

/** Percent inputs are stored as fractions (0.04 = 4%); edit in human units. */
function toEdit(unit: MetricUnit, v: number): number {
  return unit === "percent" ? Math.round(v * 1000) / 10 : v;
}
function fromEdit(unit: MetricUnit, v: number): number {
  return unit === "percent" ? v / 100 : v;
}

/** Actual-vs-target comparison as a hairline-divided grid cell (not a floating card). */
export function BenchmarkCell({
  label, actual, target, unit, higherIsBetter, note, money, onSaveTarget, editValue,
}: BenchmarkCellProps) {
  const pct = actual != null && target != null && target > 0 ? actual / target : null;
  const track = target == null ? null : computeOnTrack(actual, target, higherIsBetter);
  const fill = pct == null ? 0 : Math.min(100, Math.max(2, pct * 100));

  const raw = editValue ?? target;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  function start() {
    setDraft(raw == null ? "" : String(toEdit(unit, raw)));
    setEditing(true);
  }
  async function commit() {
    const n = Number(draft.replace(",", "."));
    if (!Number.isFinite(n)) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSaveTarget?.(fromEdit(unit, n));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="group/cell min-w-0 bg-card p-5">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{label}</p>
          {note && <p className="truncate text-xs text-muted-foreground/70">{note}</p>}
        </div>
        <Badge variant={track == null ? "outline" : track ? "success" : "warning"} size="sm">
          {track == null ? "—" : track ? "Op koers" : "Onder doel"}
        </Badge>
      </div>
      <div className="mt-3 flex items-end justify-between gap-2">
        <span className="font-heading text-xl font-semibold tabular-nums">{formatMetric(actual, unit, money)}</span>
        {editing ? (
          <span className="flex items-center gap-1">
            {unit === "currency" && <span className="text-xs text-muted-foreground">€</span>}
            <input
              autoFocus
              type="text"
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
              disabled={saving}
              className="h-7 w-20 rounded-md border border-input bg-background px-2 text-right text-xs tabular-nums outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/24"
            />
            {unit === "percent" && <span className="text-xs text-muted-foreground">%</span>}
            <button type="button" onClick={commit} disabled={saving} aria-label="Opslaan"
              className="grid h-6 w-6 place-items-center rounded-md text-success hover:bg-success/10 disabled:opacity-50">
              <HugeiconsIcon icon={Tick02Icon} size={14} />
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={saving} aria-label="Annuleren"
              className="grid h-6 w-6 place-items-center rounded-md text-muted-foreground hover:bg-muted">
              <HugeiconsIcon icon={Cancel01Icon} size={14} />
            </button>
          </span>
        ) : (
          <span className="flex items-center gap-1.5">
            {target == null ? (
              onSaveTarget ? (
                <button type="button" onClick={start}
                  className="rounded-md px-1.5 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10">
                  Doel instellen
                </button>
              ) : (
                <span className="text-xs text-muted-foreground">geen doel ingesteld</span>
              )
            ) : (
              <>
                <span className="text-xs text-muted-foreground">doel {formatMetric(target, unit, money)}</span>
                {onSaveTarget && (
                  <button type="button" onClick={start} aria-label="Doel aanpassen"
                    className="grid h-6 w-6 place-items-center rounded-md text-muted-foreground/60 opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/cell:opacity-100">
                    <HugeiconsIcon icon={PencilEditIcon} size={13} />
                  </button>
                )}
              </>
            )}
          </span>
        )}
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        {target != null && (
          <div
            className={cn("h-full rounded-full transition-all", track ? "bg-success" : "bg-warning")}
            style={{ width: `${fill}%` }}
          />
        )}
      </div>
    </div>
  );
}

/** A titled section card wrapping a divided grid of benchmark cells. */
export function BenchmarkSection({
  title, icon: Icon, count, columns = 3, children,
}: { title: string; icon?: IconSvgElement; count?: number; columns?: 1 | 2 | 3; children: React.ReactNode }) {
  const grid = columns === 1 ? "" : columns === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3";
  return (
    <Card className="overflow-hidden p-0">
      <div className="band-header flex items-center gap-2.5 px-5 py-4">
        {Icon && (
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15">
            <HugeiconsIcon icon={Icon} size={18} />
          </span>
        )}
        <h3 className="font-heading text-base font-semibold">{title}</h3>
        {count != null && <Badge variant="outline" size="sm" className="ml-auto">{count}</Badge>}
      </div>
      <div>
      <div className={`grid grid-cols-1 gap-px overflow-hidden rounded-t-xl bg-border ${grid}`}>{children}</div>
      </div>
    </Card>
  );
}
